import { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { EventUnionWithPermissions } from '../../../../types/events.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import {
  collectLinkedSignatureFileLinks,
  excludeDocusignFileLinks,
  resolveTransactionLinkedSignatureFileLinks,
} from '../utils/resolve-linked-signature-file-links.util';
import { EventDocusignDocPreviewLinks } from './event-docusign-doc-preview-links';

type TransactionFile = {
  doc?: { name?: string };
  docLink?: string;
};

export type EventTransactionAttachedFilesPreviewProps = {
  event: EventUnionWithPermissions | null;
  files?: TransactionFile[];
  filesLinks?: string[];
  onDocumentClick?: (fileLink: string) => void;
  previewMode?: boolean;
  transactionId?: string;
};

export const EventTransactionAttachedFilesPreview: FC<
  EventTransactionAttachedFilesPreviewProps
> = ({ event, files, filesLinks, onDocumentClick, previewMode, transactionId }) => {
  const docusignLinks = resolveTransactionLinkedSignatureFileLinks(event, transactionId);
  const hasDocusign = docusignLinks.length > 0;
  const allDocusignLinks = collectLinkedSignatureFileLinks(event, { transactionId });

  const manualItems = files?.length
    ? (files ?? [])
        .filter(({ docLink }) => docLink && !allDocusignLinks.includes(docLink))
        .map(({ doc, docLink }) => ({
          label: doc?.name ?? getS3FileOriginalName(docLink!) ?? docLink!,
          link: docLink!,
        }))
    : excludeDocusignFileLinks(filesLinks ?? [], allDocusignLinks).map((link) => ({
        label: getS3FileOriginalName(link) || link,
        link,
      }));

  const renderAttachedFileLinks = () => {
    if (manualItems.length === 0) {
      return <span className="truncate text-xs font-[450] text-gray-700">-</span>;
    }

    return manualItems.map(({ label, link }) => (
      <StorageDocumentLink key={link} label={label} link={link} onDocumentClick={onDocumentClick} />
    ));
  };

  const fileListClassName = 'flex min-w-0 flex-1 flex-col gap-2 overflow-hidden';
  const summaryValueClassName = 'min-w-0 flex-1 max-w-none overflow-hidden whitespace-normal';

  return (
    <>
      <SummaryItem
        className={hasDocusign ? 'border-b-[1px] border-gray-100' : 'pb-0'}
        title={<AppFormattedMessage id="ATTACHED_FILES_TITLE" />}
        titleClassName="w-fit"
        titleWrapperClassName="w-[120px]"
        value={<div className={fileListClassName}>{renderAttachedFileLinks()}</div>}
        valueClassName={summaryValueClassName}
      />
      {hasDocusign && (
        <SummaryItem
          className="pb-0"
          title={<AppFormattedMessage id="DOCUSIGN_DOC" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={
            <EventDocusignDocPreviewLinks
              fileLinks={docusignLinks}
              onDocumentClick={onDocumentClick}
              previewMode={previewMode}
            />
          }
          valueClassName={summaryValueClassName}
        />
      )}
    </>
  );
};
